<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {useStore} from 'vuex'
import {formatTime} from "@/utils/datetime"

import {bbox} from '@turf/turf'
import {CheckCircle2, XCircle, Clock} from 'lucide-vue-next'

import {Spinner} from '@/components/ui/spinner'
import { MapSource, MapLayer } from '@/components/map'
import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'


const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const layerManager = useLayerManager(map)
const settings = computed(() => store.getters['user/getUserSettings'])

const results = ref([])
const activeResult = ref(null)

// Get subscription results from the store (batch-fetched)
const subscriptionResults = computed(() =>
  store.getters['layers/getSubscriptionResults'](props.orgId, props.code)
)

const emit = defineEmits(['isLoaded'])
const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  options: {
    type: Object,
    required: true
  },
  layer: {
    type: Object,
    required: true
  },
  isVisible: {
    type: Boolean,
    required: true
  },
  code: {
    type: String,
    required: true
  },
  orgId: {
    type: Number,
    required: true
  }
})

// we don't need to re-render the map source and layer when the layer is
// not visible, it will be handled by the parent component and mapbox api
const isVisibleChecked = ref(false)

const feature = computed(() => {
  return {
    type: 'Feature',
    geometry: props.layer.geometry,
  }
})

const statusToLabel = {
  '100': 'Pending Approval',
  '-100': 'Rejected',
  '-9': 'Preparing Data (Failed)',
  '-8': 'Cleaning Up (Failed)',
  '-7': 'Uploading Tiles (Failed)',
  '-6': 'Generating Tiles (Failed)',
  '-5': 'Merging Quads (Failed)',
  '-4': 'Uploading Data (Failed)',
  '-3': 'Delivering Data (Failed)',
  '-2': 'Ordering Data (Failed)',
  '-1': 'Starting (Failed)',
  '0': 'Done',
  '1': 'Starting',
  '2': 'Ordering Data',
  '3': 'Delivering Data',
  '4': 'Uploading Data',
  '5': 'Merging Quads',
  '6': 'Generating Tiles',
  '7': 'Uploading Tiles',
  '8': 'Cleaning Up',
  '9': 'Preparing Data',
  '10': 'Generating PNG'
}


/**
 * Generate a human-readable label from a Planet resource_id.
 * Parses patterns like "ABOVEGROUND_CARBON_DENSITY_v1.0.0_30" or "LST-AMSR2_V1.0_100"
 *
 * @param {string} resourceId - The Planet resource identifier
 * @returns {string} Human-readable label
 */
const generateResourceLabel = (resourceId) => {
  if (!resourceId) return 'Unknown'

  // Try to match pattern: NAME_vX.X.X_RES or NAME_VX.X_RES
  const match = resourceId.match(/^(.+?)_([vV][\d.]+)_(\d+)$/)

  if (match) {
    const [, name, version, resolution] = match
    // Convert underscores/hyphens to spaces and title case
    const labelName = name
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')

    return `${labelName} ${resolution}m (${version})`
  }

  // Fallback: just format the resource_id
  return resourceId
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Computed label for the current layer's resource_id
const resourceLabel = computed(() => {
  const resourceId = props.layer.data?.parameters?.id
  return generateResourceLabel(resourceId)
})

const box = bbox(feature.value)
const topLeft = [box[0], box[3]]
const topRight = [box[2], box[3]]
const bottomRight = [box[2], box[1]]
const bottomLeft = [box[0], box[1]]
const corners = [
  topLeft,
  topRight,
  bottomRight,
  bottomLeft
]

// Unified layer ID helpers for multiple results
const getRasterSourceId = (resultId) => createSourceId(props.groupId, `raster-source-${resultId}`)
const getRasterLayerId = (resultId) => createLayerId(props.groupId, `raster-${resultId}`)

// Track which layer is currently registered
const registeredLayerId = ref(null)

const initializeResults = (rawResults) => {
  if (!rawResults?.length) return

  const subscriptionId = props.layer?.planet_subscription_id
  const sortedResults = [...rawResults].sort((a, b) => new Date(a.item_datetime) - new Date(b.item_datetime))

  results.value = sortedResults.map((i) => ({
    ...i,
    s3_url: `https://planet-basemaps.s3.us-west-2.amazonaws.com/layer-${subscriptionId}-${i.id}.png`,
    isRendered: false
  }))

  activeResult.value = results.value[0]

  // Register the active layer
  if (activeResult.value) {
    const layerId = getRasterLayerId(activeResult.value.id)
    layerManager.registerLayer(props.groupId, layerId)
    registeredLayerId.value = layerId
  }

  emit('isLoaded', true)
}

onMounted(() => {
  if (props.layer.status !== 0) return

  // Use results from store if available (batch-fetched)
  if (subscriptionResults.value) {
    initializeResults(subscriptionResults.value)
  }
})

// Watch for subscription results to be loaded from store (async batch fetch)
watch(subscriptionResults, (newResults) => {
  if (newResults && props.layer.status === 0 && !results.value.length) {
    initializeResults(newResults)
  }
})

onUnmounted(() => {
  // Unregister the current layer
  if (registeredLayerId.value) {
    layerManager.unregisterLayer(props.groupId, registeredLayerId.value)
  }
})

watch(() => props.isVisible, (value) => {
  if (value) {
    isVisibleChecked.value = true
  }
})

// When active result changes, update registered layer
watch(activeResult, (newResult, oldResult) => {
  if (oldResult && registeredLayerId.value) {
    layerManager.unregisterLayer(props.groupId, registeredLayerId.value)
  }
  if (newResult) {
    const layerId = getRasterLayerId(newResult.id)
    layerManager.registerLayer(props.groupId, layerId)
    registeredLayerId.value = layerId
  }
})

// Watch for active result changes from the store (triggered by chart hover in Layer Detail)
const storeActiveResult = computed(() => store.getters['layers/getActiveResult'](props.code))
watch(storeActiveResult, (newResult) => {
  if (newResult && results.value.length > 0) {
    // Find the matching result in our local results array
    const matchingResult = results.value.find(r => r.id === newResult.id)
    if (matchingResult && activeResult.value?.id !== matchingResult.id) {
      activeResult.value = matchingResult
    }
  }
})
</script>

<template>
  <div v-if="isVisibleChecked">
    <MapSource
      v-if="map"
      v-for="result in results"
      :key="result.id"
      :map="map"
      type="image"
      :id="getRasterSourceId(result.id)"
      :url="result.s3_url"
      :coordinates="corners"
    >
      <MapLayer
        v-if="result.id === activeResult.id"
        :map="map"
        :id="getRasterLayerId(result.id)"
        :source="getRasterSourceId(result.id)"
        type="raster"
        :options="{
          ...props.options,
          paint: {
            'raster-fade-duration': 0,
            'raster-resampling': 'nearest'
          }
        }"
        :before-id="beforeId"
        :group-id="groupId"
        @loaded="() => { result.isRendered = true }"
      ></MapLayer>
      <div v-if="!result.isRendered && result.id === activeResult.id" class="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-white/50">
        <Spinner class="w-5 h-5 mr-2" />
        Loading Data
      </div>
    </MapSource>
  </div>
</template>
